import { createQueryKey } from '../query/createQueryKeyFactory';
import { QueryKey, QueryKeyParam } from '../query/types';
import { backerConsumerBackerKeys } from './consumer/backer/keys';
import { consumerCommunicationStatusKeys } from './consumer/communicationStatus/keys';
import { consumerDirectoryKeys } from './consumer/directory/keys';
import { familiesKeys } from './consumer/families/keys';
import { consumerListsKeys } from './consumer/lists/keys';
import { consumerPropertiesKeys } from './consumer/properties/keys';
import { consumerWalletKeys } from './consumer/wallet/keys';
import { causeBackerFamiliesKeys } from './families/keys';
import { listsKeys } from './lists/keys';
import { widgetsKeys } from './widgets/keys';

const causeBackerKeys = <T extends QueryKey>(keys: T) =>
  createQueryKey({
    all: [...keys] as const,
    id: <U extends QueryKeyParam>(id: U) =>
      createQueryKey({
        all: [...keys, id] as const,
        lists: [...keys, id, 'lists'] as const,
        families: causeBackerFamiliesKeys([...keys, id] as const),
      }),
  });

const propertiesKeys = <T extends QueryKey>(keys: T) =>
  createQueryKey({
    all: [...keys] as const,
    id: <U extends QueryKeyParam>(causeId: U) =>
      createQueryKey({
        all: [...keys, causeId] as const,
      }),
  });

const causesKeys = <T extends QueryKey>(keys: T) =>
  createQueryKey({
    all: [...keys] as const,
    id: <U extends QueryKeyParam>(id: U) =>
      createQueryKey({
        all: [...keys, id] as const,
        backer: [...keys, id, 'backer'] as const,
        recipients: [...keys, id, 'recipients'] as const,
        recipientsList: [...keys, id, 'recipients', 'list'] as const,
        backers: causeBackerKeys([...keys, id, 'backers'] as const),
        backersExport: [...keys, id, 'backers', 'export'] as const,
        lists: listsKeys([...keys, id, 'lists'] as const),
        properties: propertiesKeys([...keys, id, 'properties'] as const),
        emailCount: [...keys, id, 'emails', 'count'] as const,
        phoneNumberCount: [...keys, id, 'phoneNumbers', 'count'] as const,
        widgets: widgetsKeys([...keys, id, 'widgets'] as const),
        families: causeBackerFamiliesKeys([...keys, id, 'families'] as const),
      }),
  });

const consumerCausesKeys = <T extends QueryKey>(keys: T) =>
  createQueryKey({
    all: [...keys] as const,
    id: <U extends QueryKeyParam>(causeId: U) =>
      createQueryKey({
        all: [...keys, causeId] as const,
        directory: consumerDirectoryKeys([...keys, 'directory', causeId] as const),
      }),
  });

const consumerKeys = <T extends QueryKey>(keys: T) =>
  createQueryKey({
    all: [...keys] as const,
    causes: consumerCausesKeys([...keys, 'causes'] as const),
    communicationStatus: consumerCommunicationStatusKeys(['communicationStatus'] as const),
    lists: consumerListsKeys(['lists'] as const),
    properties: consumerPropertiesKeys([...keys, 'properties', 'causes'] as const),
    backer: backerConsumerBackerKeys(keys),
    families: familiesKeys(keys),
  });

const backerKeys = createQueryKey({
  all: ['backer'] as const,
  me: ['backer', 'me'] as const,
  causes: causesKeys(['backer', 'causes'] as const),
  consumer: consumerKeys(['backer', 'consumer'] as const),
  consumerWallet: consumerWalletKeys(['backer'] as const),
});

export { backerKeys };
